import { useGetCompany } from "@/entities/get-company/fetch";
import { useAuth } from "@/shared/auth/fetch/useAuth";
import { useNavigate, useRouter } from "@tanstack/react-router";
import * as React from "react";
import type { Except } from "type-fest";
import type { CheckAuthAndRedirectProps } from "../model";

export const useCheckAuthAndRedirectProps = ({
  pathNotFound,
}: { pathNotFound?: boolean }): Except<
  CheckAuthAndRedirectProps,
  "children"
> => {
  const auth = useAuth();
  const getCompanyQuery = useGetCompany(
    {
      id: auth.session.isAuthenticated ? auth.session.user.companyId : -1,
    },
    {
      enabled: auth.session.isAuthenticated,
    },
  );
  const router = useRouter();
  const currentPath = router.state.location.pathname;
  const navigate = useNavigate();
  React.useEffect(() => {
    if (!auth.session.isAuthenticated) {
      navigate({
        to: "/login",
      });
      return;
    }
    if (!pathNotFound && currentPath !== "/") {
      return;
    }
    if (
      getCompanyQuery.status === "pending" ||
      getCompanyQuery.status === "error"
    ) {
      return;
    }
    if (getCompanyQuery.data.data.category?.includes("shipper")) {
      navigate({
        to: "/shipper/cargos",
      });
      return;
    }
    if (getCompanyQuery.data.data.category?.includes("carrier")) {
      navigate({
        to: "/carrier/cargos",
        search: {
          tab: "not_proposed",
        },
      });
      return;
    }
  }, [
    auth,
    getCompanyQuery.data,
    getCompanyQuery.status,
    navigate,
    pathNotFound,
    currentPath,
  ]);

  return React.useMemo(() => {
    if (!auth.session.isAuthenticated) {
      return {
        authenticateStatus: "unauthenticated",
        message: undefined,
      };
    }
    if (getCompanyQuery.status === "pending") {
      return {
        authenticateStatus: "loading",
        message: undefined,
      };
    }
    if (getCompanyQuery.status === "error") {
      return {
        authenticateStatus: "error",
        message: JSON.stringify(getCompanyQuery.error),
      };
    }
    return {
      authenticateStatus: "success",
      message: undefined,
    };
  }, [
    getCompanyQuery.status,
    getCompanyQuery.error,
    auth.session.isAuthenticated,
  ]);
};
